import { Box, type Theme } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { type UseModalState } from "@src/lib/ui-react";

import { InfoBanner } from "../../../components/InfoBanner/InfoBanner";
import { type InviteBlockReason } from "./types";

interface InviteBlockedModalProps {
  modalState: UseModalState;
  blockReason: InviteBlockReason;
  onClose: () => void;
}

/**
 * Modal displayed when the entire invite flow is blocked.
 * This happens when the time slot is in the past (maxSlotStartTime - 2 hours < currentTime).
 *
 * Per spec: Shows message explaining user can't send invites for past shifts
 * and suggests the next valid slot they can use.
 */
export function InviteBlockedModal(props: InviteBlockedModalProps) {
  const { modalState, blockReason, onClose } = props;

  const handleClose = () => {
    modalState.closeModal();
    onClose();
  };

  return (
    <ResponsiveDialog
      dismissible={false}
      modalState={modalState}
      title="Cannot send invites"
      footerContent={
        <DialogFooter
          layout="horizontal"
          primaryAction={{
            label: "Got it",
            onClick: handleClose,
          }}
        />
      }
      onClose={handleClose}
    >
      <Box
        sx={(theme: Theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(4),
        })}
      >
        <InfoBanner message={blockReason.message} title="Time slot unavailable" variant="error" />

        <Text
          sx={(theme: Theme) => ({
            color: theme.palette.text?.secondary,
          })}
          variant="body2"
        >
          Please select a different time slot to send invites.
        </Text>
      </Box>
    </ResponsiveDialog>
  );
}
